import React, { useMemo } from "react";
import { Box, LinearProgress, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { getSLAByClassification, DEFAULT_SLA } from "../../../../../config/slaConfig";
import Icon from "../../../../Icon/Icon";

/**
 * Calculate business days between two dates (excludes weekends)
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @returns {number} - Number of business days
 */
const calculateBusinessDays = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;

  const start = new Date(startDate);
  const end = new Date(endDate);

  // Ensure start is before end
  if (start > end) return 0;

  let count = 0;
  const current = new Date(start);

  while (current <= end) {
    const dayOfWeek = current.getDay();
    // 0 = Sunday, 6 = Saturday
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      count++;
    }
    current.setDate(current.getDate() + 1);
  }

  return count;
};

/**
 * Find the date when status changed to "process"
 * Checks both current status and history
 * @param {object} status - Status object with current, assigned, and history
 * @returns {Date|null} - Date when process started
 */
const findProcessStartDate = (status) => {
  if (!status) return null;

  // First check in history
  const history = status.history || [];
  const processEntry = history.find((item) => item.status === "process");
  if (processEntry?.assigned) {
    return new Date(processEntry.assigned);
  }

  // If not in history, check if current status is "process"
  if (status.current === "process" && status.assigned) {
    return new Date(status.assigned);
  }

  return null;
};

/**
 * Find the date when status changed to "closed"
 * Checks both current status and history
 * @param {object} status - Status object with current, assigned, and history
 * @returns {Date|null} - Date when closed
 */
const findClosedDate = (status) => {
  if (!status) return null;

  // First check if current status is "closed"
  if (status.current === "closed" && status.assigned) {
    return new Date(status.assigned);
  }

  // Otherwise look in history
  const history = status.history || [];
  const closedEntry = history.find((item) => item.status === "closed");
  return closedEntry?.assigned ? new Date(closedEntry.assigned) : null;
};

export const SLAIndicator = ({ reportDetail }) => {
  const [t] = useTranslation("report");
  const theme = useTheme();

  const slaData = useMemo(() => {
    const classification = reportDetail?.classification;
    const status = reportDetail?.status;
    const currentStatus = status?.current;

    // Get SLA for this classification
    const slaDays = getSLAByClassification(classification) || DEFAULT_SLA;

    // Find when process started (checks both current and history)
    const processStartDate = findProcessStartDate(status);

    // If never in process, don't show SLA
    if (!processStartDate) {
      return null;
    }

    // Determine end date: if closed, use close date; otherwise use today
    let endDate;
    let isClosed = false;
    if (currentStatus === "closed") {
      endDate = findClosedDate(status) || new Date();
      isClosed = true;
    } else {
      endDate = new Date();
    }

    // Calculate business days elapsed
    const daysElapsed = calculateBusinessDays(processStartDate, endDate);

    // Calculate percentage
    const percentage = Math.min((daysElapsed / slaDays) * 100, 100);

    // Determine SLA status
    let slaStatus = "on_track"; // green
    if (percentage >= 100) {
      slaStatus = "exceeded"; // red
    } else if (percentage >= 75) {
      slaStatus = "warning"; // yellow/orange
    }

    return {
      slaDays,
      daysElapsed,
      daysRemaining: Math.max(slaDays - daysElapsed, 0),
      percentage,
      slaStatus,
      isClosed,
    };
  }, [reportDetail]);

  // Don't render if no SLA data (not in process yet)
  if (!slaData) {
    return null;
  }

  // Color mapping based on SLA status
  const getStatusColor = () => {
    switch (slaData.slaStatus) {
      case "exceeded":
        return theme.palette.error.main;
      case "warning":
        return theme.palette.warning.main;
      default:
        return theme.palette.success.main;
    }
  };

  const getStatusLabel = () => {
    if (slaData.isClosed) {
      if (slaData.slaStatus === "exceeded") {
        return t("Care.sla_exceeded_closed");
      }
      return t("Care.sla_completed_on_time");
    }
    switch (slaData.slaStatus) {
      case "exceeded":
        return t("Care.sla_exceeded");
      case "warning":
        return t("Care.sla_warning");
      default:
        return t("Care.sla_on_track");
    }
  };

  const statusColor = getStatusColor();

  return (
    <Box
      sx={{
        p: 2,
        mt: 2,
        mb: 2,
        borderRadius: "8px",
        border: `1px solid ${theme.palette.blueGray200.main}`,
        backgroundColor: theme.palette.blueGray50.main,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Icon name="clock" size={20} color={statusColor} />
          <Typography fontSize={14} fontWeight={600} color="secondary">
            {t("Care.sla_title")}
          </Typography>
        </Stack>
        <Typography
          fontSize={12}
          fontWeight={600}
          sx={{
            color: statusColor,
            backgroundColor: `${statusColor}15`,
            px: 1.5,
            py: 0.5,
            borderRadius: "4px",
          }}
        >
          {getStatusLabel()}
        </Typography>
      </Stack>

      <LinearProgress
        variant="determinate"
        value={slaData.percentage}
        sx={{
          height: 8,
          borderRadius: 4,
          backgroundColor: theme.palette.blueGray200.main,
          "& .MuiLinearProgress-bar": {
            backgroundColor: statusColor,
            borderRadius: 4,
          },
        }}
      />

      <Stack direction="row" justifyContent="space-between" mt={1}>
        <Typography fontSize={12} color="secondary">
          {t("Care.sla_days_elapsed", { days: slaData.daysElapsed })}
        </Typography>
        <Typography fontSize={12} color="secondary">
          {slaData.isClosed
            ? t("Care.sla_total", { days: slaData.slaDays })
            : t("Care.sla_days_remaining", { days: slaData.daysRemaining })}
        </Typography>
      </Stack>

      <Typography fontSize={11} color="blueGray600.main" mt={0.5}>
        {t("Care.sla_business_days_note")}
      </Typography>
    </Box>
  );
};
